/**
 * Settings Screen
 * 
 * Provides configuration options for the booth app, including:
 * - Current outlet and role information
 * - Change outlet/role functionality
 * - App version and sync status
 * - Clear cache option
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getRecentLogs, type AuthChangeLog } from '../../lib/auth/auth-logger';
import { getCameraConfig, setCameraConfig } from '../../lib/config/cameras';
import { PinConfirmModal } from '../../components/PinConfirmModal';
import {
  getTerminalUrlsConfig,
  setTerminalUrlsConfig,
  getEffectiveApiBaseUrl,
  getEffectiveBridgeBaseUrl,
} from '../../lib/config/terminal-urls';
import { refreshFramesCache } from '../../lib/db/frames';

interface VideoDevice {
  deviceId: string;
  label: string;
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { authState, outlet, clearAuthentication } = useOutletAuth();
  const [pinVerified, setPinVerified] = useState(false);
  const [showChangeOutletModal, setShowChangeOutletModal] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const recentLogs = getRecentLogs(5);

  const pinRole = authState?.role === 'capture' ? 'capture' : 'customer';

  const [videoDevices, setVideoDevices] = useState<VideoDevice[]>([]);
  const [qrCameraId, setQrCameraId] = useState<string>('');
  const [photoCameraId, setPhotoCameraId] = useState<string>('');
  const [cameraConfigSaved, setCameraConfigSaved] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [bridgeUrl, setBridgeUrl] = useState('');
  const [urlConfigSaved, setUrlConfigSaved] = useState(false);
  const [effectiveApiUrl, setEffectiveApiUrl] = useState('');
  const [framesRefreshLoading, setFramesRefreshLoading] = useState(false);
  const [framesRefreshMessage, setFramesRefreshMessage] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    const load = async () => {
      const config = getCameraConfig();
      setQrCameraId(config.qrCameraDeviceId ?? '');
      setPhotoCameraId(config.photoCameraDeviceId ?? '');

      // Load terminal URL config
      const urls = getTerminalUrlsConfig();
      const effectiveApi = getEffectiveApiBaseUrl();
      const effectiveBridge = getEffectiveBridgeBaseUrl();
      setApiUrl(urls.apiBaseUrl ?? effectiveApi);
      setBridgeUrl(urls.bridgeBaseUrl ?? effectiveBridge);
      setEffectiveApiUrl(effectiveApi);

      if (!navigator.mediaDevices?.enumerateDevices) return;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const video = devices
          .filter((d) => d.kind === 'videoinput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 8)}` }));
        setVideoDevices(video);
      } catch {
        setVideoDevices([]);
      }
    };
    load();
  }, []);

  const handleSaveCameraConfig = () => {
    setCameraConfig({
      qrCameraDeviceId: qrCameraId || null,
      photoCameraDeviceId: photoCameraId || null,
    });
    setCameraConfigSaved(true);
    setTimeout(() => setCameraConfigSaved(false), 2000);
  };

  const handleSaveUrlConfig = () => {
    setTerminalUrlsConfig({
      apiBaseUrl: apiUrl.trim() || null,
      bridgeBaseUrl: bridgeUrl.trim() || null,
    });
    setUrlConfigSaved(true);
    setTimeout(() => setUrlConfigSaved(false), 2000);
  };

  const handleRefreshFramesCache = async () => {
    setFramesRefreshMessage(null);
    setFramesRefreshLoading(true);
    try {
      await refreshFramesCache();
      setFramesRefreshMessage('success');
      setTimeout(() => setFramesRefreshMessage(null), 3000);
    } catch (err) {
      console.error('Refresh frames cache failed:', err);
      setFramesRefreshMessage('error');
      setTimeout(() => setFramesRefreshMessage(null), 5000);
    } finally {
      setFramesRefreshLoading(false);
    }
  };

  const handleBack = () => {
    // Navigate to appropriate home based on role
    if (authState?.role === 'customer') {
      navigate('/');
    } else if (authState?.role === 'capture') {
      navigate('/capture');
    } else if (authState?.role === 'cashier') {
      navigate('/cashier');
    }
  };

  const handleChangeOutlet = () => {
    setShowChangeOutletModal(true);
  };

  const handleConfirmChange = async () => {
    // Clear authentication and navigate to auth screen
    await clearAuthentication();
    navigate('/auth');
  };

  // PIN required to access Settings (same as logout confirmation)
  if (authState?.outletId && !pinVerified) {
    return (
      <PinConfirmModal
        outletId={authState.outletId}
        pinRole={pinRole}
        title="Access Settings"
        message="Enter the outlet PIN to open Settings."
        confirmLabel="Continue"
        onClose={handleBack}
        onConfirm={() => setPinVerified(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
          >
            Back
          </button>
        </div>

        {/* Current Configuration */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Current Configuration</h2>
          
          <div className="space-y-4">
            {/* Outlet Information */}
            <div className="border-b pb-4">
              <div className="text-sm text-gray-500 mb-1">Outlet</div>
              <div className="text-lg font-semibold text-gray-800">
                {outlet?.name || authState?.outletName || 'Unknown'}
              </div>
              <div className="text-sm text-gray-600">
                {outlet?.location || authState?.outletLocation || 'Unknown location'}
              </div>
            </div>

            {/* Role Information */}
            <div className="border-b pb-4">
              <div className="text-sm text-gray-500 mb-1">Device Role</div>
              <div className="text-lg font-semibold text-gray-800 capitalize">
                {authState?.role || 'Unknown'}
              </div>
              <div className="text-sm text-gray-600">
                {authState?.role === 'customer' && 'Order and Edit screens'}
                {authState?.role === 'capture' && 'Photo Capture screen'}
                {authState?.role === 'cashier' && 'Cashier Management Interface'}
              </div>
            </div>

            {/* Authentication Time */}
            <div>
              <div className="text-sm text-gray-500 mb-1">Authenticated At</div>
              <div className="text-sm text-gray-700">
                {authState?.authenticatedAt 
                  ? new Date(authState.authenticatedAt).toLocaleString()
                  : 'Unknown'}
              </div>
            </div>
          </div>
        </div>

        {/* App Information */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">App Information</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Version</span>
              <span className="text-gray-800 font-semibold">
                {import.meta.env.VITE_APP_VERSION || '1.0.0'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Environment</span>
              <span className="text-gray-800 font-semibold">
                {import.meta.env.MODE || 'production'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">API URL</span>
              <span className="text-gray-800 font-semibold text-sm">
                {effectiveApiUrl || getEffectiveApiBaseUrl()}
              </span>
            </div>
          </div>
        </div>

        {/* Network & API Settings */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Network & API Settings</h2>
          <p className="text-sm text-gray-600 mb-4">
            Configure which API server and Bridge (host) this terminal talks to. These settings apply only to this device.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API base URL
              </label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder={import.meta.env.VITE_API_URL || 'http://localhost:3001'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Example: http://192.168.1.100:3001
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bridge / Host URL
              </label>
              <input
                type="text"
                value={bridgeUrl}
                onChange={(e) => setBridgeUrl(e.target.value)}
                placeholder={import.meta.env.VITE_BRIDGE_URL || 'http://localhost:3001'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Example: http://192.168.1.10:3001 (use IP shown in Bridge app)
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveUrlConfig}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
            >
              {urlConfigSaved ? 'Saved' : 'Save network settings'}
            </button>
          </div>
        </div>

        {/* Data & Cache - refresh IndexedDB frame cache */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Data & Cache</h2>
          <p className="text-sm text-gray-600 mb-4">
            Frames used in the edit terminal are cached in IndexedDB. If you upload new frames in the admin dashboard, use this to refresh the cache so they appear on this device.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={handleRefreshFramesCache}
              disabled={framesRefreshLoading}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-lg font-medium transition-colors"
            >
              {framesRefreshLoading ? 'Refreshing…' : 'Refresh frame cache'}
            </button>
            {framesRefreshMessage === 'success' && (
              <span className="text-sm text-green-600 font-medium">Frame cache refreshed.</span>
            )}
            {framesRefreshMessage === 'error' && (
              <span className="text-sm text-red-600 font-medium">Refresh failed. Check network and try again.</span>
            )}
          </div>
        </div>

        {/* Camera configuration (local per device) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Camera Configuration</h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose which camera to use for QR scanning and for photo capture. Stored on this device only.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">QR scan camera</label>
              <select
                value={qrCameraId}
                onChange={(e) => setQrCameraId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Default (environment / first available)</option>
                {videoDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Photo capture camera</label>
              <select
                value={photoCameraId}
                onChange={(e) => setPhotoCameraId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Default (first available)</option>
                {videoDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleSaveCameraConfig}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
            >
              {cameraConfigSaved ? 'Saved' : 'Save camera config'}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Actions</h2>
          
          <div className="space-y-3">
            <button
              onClick={handleChangeOutlet}
              className="w-full px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors text-left flex items-center justify-between"
            >
              <div>
                <div className="font-bold">Change Outlet or Role</div>
                <div className="text-sm text-blue-100">
                  Re-authenticate with a different outlet or role
                </div>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Authentication History */}
        {recentLogs.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Recent Activity</h2>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="text-sm text-blue-500 hover:text-blue-600 font-semibold"
              >
                {showLogs ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showLogs && (
              <div className="space-y-3">
                {recentLogs.map((log, index) => (
                  <AuthLogEntry key={index} log={log} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Change Outlet Modal */}
        {showChangeOutletModal && (
          <ChangeOutletModal
            currentOutletId={authState?.outletId || ''}
            onClose={() => setShowChangeOutletModal(false)}
            onConfirm={handleConfirmChange}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Modal for confirming outlet change with PIN verification
 */
interface ChangeOutletModalProps {
  currentOutletId: string;
  onClose: () => void;
  onConfirm: () => void;
}

function ChangeOutletModal({ currentOutletId, onClose, onConfirm }: ChangeOutletModalProps) {
  const [step, setStep] = useState<'confirm' | 'pin'>('confirm');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = () => {
    setStep('pin');
    setError(null);
  };

  const handlePinSubmit = async () => {
    if (pin.length < 4 || pin.length > 6) {
      setError('PIN must be 4-6 digits');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validate PIN with API
      const apiClient = createPhotoBoothAPI({
        baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3001',
      });

      // For outlet change we validate against the Customer PIN
      // that is configured on the outlet in the admin dashboard.
      const response = await apiClient.outlets.validatePin(currentOutletId, pin, 'customer');

      if (!response.success || !response.data?.valid) {
        setError(response.data?.message || 'Invalid PIN. Please try again.');
        setPin('');
        setIsLoading(false);
        return;
      }

      // PIN is valid, proceed with outlet change
      onConfirm();
    } catch (err) {
      console.error('PIN validation error:', err);
      setError('Failed to validate PIN. Please try again.');
      setPin('');
      setIsLoading(false);
    }
  };

  const handleKeypadPress = (key: string) => {
    if (isLoading) return;

    if (key === 'clear') {
      setPin('');
    } else if (key === 'backspace') {
      setPin(prev => prev.slice(0, -1));
    } else if (pin.length < 6) {
      setPin(prev => prev + key);
    }
  };

  const handleKeyboardInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLoading) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Enter' && pin.length >= 4) {
      handlePinSubmit();
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      return;
    } else if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
  };

  const KEYPAD_BUTTONS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'backspace'],
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {step === 'confirm' ? (
          <>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Change Outlet or Role?
            </h3>
            
            <p className="text-gray-600 mb-6">
              This will log you out and return to the authentication screen. 
              You'll need to select a new outlet and role.
            </p>

            <div className="text-sm text-gray-500 mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <strong>Note:</strong> You must verify your current outlet PIN to proceed.
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                className="flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Verify Current PIN
            </h3>
            <p className="text-gray-600 mb-6">
              Enter your current outlet PIN to confirm the change
            </p>

            {/* PIN Display */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyboardInput}
                  disabled={isLoading}
                  maxLength={6}
                  placeholder="Enter PIN"
                  className="w-full px-6 py-3 text-xl text-center font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                  autoComplete="off"
                  inputMode="numeric"
                  autoFocus
                />
                
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* PIN Length Indicator */}
              <div className="flex justify-center gap-2 mt-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i < pin.length ? 'bg-blue-500 scale-110' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                <p className="text-red-600 text-sm font-semibold">{error}</p>
              </div>
            )}

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {KEYPAD_BUTTONS.flat().map((key) => {
                if (key === 'clear') {
                  return (
                    <button
                      key={key}
                      onClick={() => handleKeypadPress(key)}
                      disabled={isLoading || pin.length === 0}
                      className="aspect-square rounded-lg bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed font-semibold text-gray-700 text-sm transition-all active:scale-95 flex items-center justify-center"
                    >
                      Clear
                    </button>
                  );
                }
                
                if (key === 'backspace') {
                  return (
                    <button
                      key={key}
                      onClick={() => handleKeypadPress(key)}
                      disabled={isLoading || pin.length === 0}
                      className="aspect-square rounded-lg bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed font-semibold text-gray-700 transition-all active:scale-95 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z" />
                      </svg>
                    </button>
                  );
                }

                return (
                  <button
                    key={key}
                    onClick={() => handleKeypadPress(key)}
                    disabled={isLoading || pin.length >= 6}
                    className="aspect-square rounded-lg bg-white border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed font-bold text-xl text-gray-800 transition-all active:scale-95"
                  >
                    {key}
                  </button>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handlePinSubmit}
                disabled={pin.length < 4 || pin.length > 6 || isLoading}
                className="flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Verifying...</span>
                  </>
                ) : (
                  'Verify & Change'
                )}
              </button>
            </div>

            <div className="mt-3 text-center text-xs text-gray-500">
              🔐 PIN must be 4-6 digits
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Display a single authentication log entry
 */
interface AuthLogEntryProps {
  log: AuthChangeLog;
}

function AuthLogEntry({ log }: AuthLogEntryProps) {
  const getChangeTypeLabel = (type: AuthChangeLog['changeType']) => {
    switch (type) {
      case 'initial_auth':
        return { label: 'Initial Authentication', color: 'text-green-600', bg: 'bg-green-50' };
      case 'outlet_change':
        return { label: 'Outlet Changed', color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'role_change':
        return { label: 'Role Changed', color: 'text-purple-600', bg: 'bg-purple-50' };
      case 'logout':
        return { label: 'Logged Out', color: 'text-gray-600', bg: 'bg-gray-50' };
      default:
        return { label: 'Unknown', color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const typeInfo = getChangeTypeLabel(log.changeType);
  const date = new Date(log.timestamp);

  return (
    <div className={`p-4 rounded-lg border ${typeInfo.bg} border-gray-200`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-sm font-semibold ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
        <span className="text-xs text-gray-500">
          {date.toLocaleString()}
        </span>
      </div>
      
      <div className="text-sm text-gray-700 space-y-1">
        {log.oldOutletId && (
          <div>
            <span className="text-gray-500">From:</span>{' '}
            <span className="font-medium">{log.oldOutletId.slice(0, 8)}...</span>
            {log.oldRole && <span className="text-gray-500"> ({log.oldRole})</span>}
          </div>
        )}
        {log.newOutletId && (
          <div>
            <span className="text-gray-500">To:</span>{' '}
            <span className="font-medium">{log.newOutletId.slice(0, 8)}...</span>
            {log.newRole && <span className="text-gray-500"> ({log.newRole})</span>}
          </div>
        )}
      </div>
    </div>
  );
}
